import React, { useMemo, useState } from 'react';
import LoginScreen from './LoginScreen';
import './LandingPage.css';

const sportsbookLogos = [
  { src: '/logos/DraftKings_logo.png', alt: 'DraftKings' },
  { src: '/logos/fanduellogo.png', alt: 'FanDuel' },
  { src: '/logos/betMGM.png', alt: 'BetMGM' },
  { src: '/logos/Bet365-Logo.png', alt: 'Bet365' },
  { src: '/logos/prizepickslogo.png', alt: 'PrizePicks' }
];

const featureCards = [
  {
    title: 'Filter-First Dashboard',
    description:
      'Stack filters for sample size, opponent splits, and custom hit-rate thresholds. The board reshapes instantly so you can audit trends without grinding spreadsheets.',
    badge: '01'
  },
  {
    title: 'Historical Performance Atlas',
    description:
      'Track how props behave across seasons, streaks, and matchup contexts. Every chart and metric is built to help researchers trust the numbers before locking a card.',
    badge: '02'
  },
  {
    title: 'Playbook For Builders',
    description:
      'Bookmark games, group props, and push curated sets into your own workflow. LinePicks keeps the data clean while you assemble the story that fits your strategy.',
    badge: '03'
  }
];

const proofPoints = [
  { label: 'Filterable stat columns', value: '40+' },
  { label: 'Historical datapoints indexed', value: '120K+' },
  { label: 'Supported leagues today', value: 'NBA • NFL' }
];

const screenshots = [
  {
    src: '/betterScreenShots/Screenshot 2025-11-29 153129.png',
    alt: 'LinePicks main dashboard',
    description: 'The main dashboard provides a comprehensive view of all available picks, with real-time data and easy navigation through games and players.'
  },
  {
    src: '/betterScreenShots/Screenshot 2025-11-29 153300.png',
    alt: 'LinePicks filter controls',
    description: 'Advanced filtering options allow you to dial in hit-rate thresholds, filter by last 5, 10, 20, or 50 games, and customize your search to find exactly what you need.'
  },
  {
    src: '/betterScreenShots/Screenshot 2025-11-29 153342.png',
    alt: 'LinePicks parlay builder',
    description: 'Build and analyze parlays with real-time probability calculations. See the implied probability of your parlay hitting before you place your bets.'
  },
  {
    src: '/betterScreenShots/Screenshot 2025-11-29 153459.png',
    alt: 'LinePicks bar charts and analytics',
    description: 'Visualize player performance with detailed bar charts showing hit rates, historical trends, and matchup-specific data to make informed decisions.'
  }
];

const LandingPage = ({ onAuthenticate, isClerkReady = true }) => {
  const [isLoginOpen, setIsLoginOpen] = useState(false);

  const handleLaunch = () => {
    setIsLoginOpen(true);
  };

  const handleCloseLogin = () => {
    setIsLoginOpen(false);
  };

  const handleLoginSuccess = () => {
    setIsLoginOpen(false);
    if (onAuthenticate) {
      onAuthenticate();
    }
  };

  const heroCopy = useMemo(
    () => ({
      headline: 'Filter Sports Data Like A Trading Desk.',
      subheading:
        'LinePicks is the research workspace for bettors who want raw numbers on command, fully filterable, matchup-aware, and ready for your card builds.'
    }),
    []
  );

  return (
    <div className="landing-page" id="top">
      <div className="landing-background">
        <div className="landing-grid-overlay" />
        <div className="landing-glow landing-glow--primary" />
        <div className="landing-glow landing-glow--secondary" />
      </div>

      <nav className="landing-nav">
        <div className="landing-brand">
          <img src="/logos/longLogo.png" alt="LinePicks" className="landing-brand-logo" />
        </div>
        <div className="landing-nav-links">
          <button type="button" onClick={() => document.getElementById('product')?.scrollIntoView({ behavior: 'smooth' })}>
            Product
          </button>
          <button type="button" onClick={() => document.getElementById('screens')?.scrollIntoView({ behavior: 'smooth' })}>
            Live Screens
          </button>
          <button type="button" onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })}>
            Why LinePicks
          </button>
        </div>
      </nav>

      <header className="landing-hero">
        <div className="landing-hero-content">
          <div className="landing-hero-copy">
            <h1>{heroCopy.headline}</h1>
            <p>{heroCopy.subheading}</p>
            <div className="landing-hero-actions">
              <button type="button" className="cta-primary" onClick={handleLaunch}>
                LAUNCH APP
              </button>
              <button
                type="button"
                className="cta-secondary"
                onClick={() => document.getElementById('screens')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Preview The Experience
              </button>
            </div>
            <div className="landing-proof">
              {proofPoints.map((item) => (
                <div key={item.label} className="proof-card">
                  <span className="proof-value">{item.value}</span>
                  <span className="proof-label">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="landing-hero-visual">
            <div className="landing-holo-card">
              <div className="holo-header">
                <span className="holo-label">Live AI Confidence Feed</span>
                <span className="holo-pulse" />
              </div>
              <div className="holo-body">
                <div className="holo-metric">
                  <span className="holo-metric-value">+38.6%</span>
                  <span className="holo-metric-label">Edge vs Consensus Line</span>
                </div>
                <div className="holo-spectrum">
                  <div className="holo-spectrum-bar" />
                  <div className="holo-spectrum-bar" />
                  <div className="holo-spectrum-bar" />
                  <div className="holo-spectrum-bar" />
                </div>
                <div className="holo-footer">
                  <span>Model Sync</span>
                  <span className="holo-status">Real-time</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <section className="landing-partners" id="about">
        <span className="partners-label">Integrated with your favorite books</span>
        <div className="partners-row">
          {sportsbookLogos.map((logo) => (
            <div key={logo.alt} className="partner-card">
              <img src={logo.src} alt={logo.alt} />
            </div>
          ))}
        </div>
      </section>

      <section className="landing-section landing-features" id="product">
        <div className="section-heading">
          <span className="eyebrow">Platform DNA</span>
          <h2>Curate the data layer your slips deserve.</h2>
          <p>
            LinePicks is built for researchers, not touts. We surface matchup context, historical splits, and trend sliders so you can make every ticket an informed move.
          </p>
        </div>
        <div className="feature-grid">
          {featureCards.map((feature) => (
            <article key={feature.title} className="feature-card">
              <span className="feature-badge">{feature.badge}</span>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="landing-section landing-showcase" id="screens">
        <div className="showcase-copy">
          <span className="eyebrow">Interface Preview</span>
          <h2>See the workspace that keeps data within reach.</h2>
          <p>
            Control the board with split views, parlay staging, and deep game drill-downs. These captures are taken straight from the LinePicks environment, no mockups.
          </p>
          <ul className="showcase-bullets">
            <li>Scale hit-rate sliders side-by-side for quick comparison.</li>
            <li>Open rich breakdowns with matchup notes and historical results.</li>
            <li>Stage parlays and tag props while monitoring implied probability.</li>
          </ul>
          <button type="button" className="cta-outline" onClick={handleLaunch}>
            Unlock Full Access
          </button>
        </div>
        <div className="showcase-screenshots">
          {screenshots.map((screenshot, index) => (
            <div key={index} className="showcase-screenshot-item">
              <div className="showcase-screenshot-wrapper">
                <img 
                  src={screenshot.src} 
                  alt={screenshot.alt} 
                  loading="lazy"
                  className="showcase-screenshot-image"
                />
                <div className="showcase-screenshot-glow" />
              </div>
              {screenshot.description && (
                <p className="showcase-screenshot-description">
                  {screenshot.description}
                </p>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="landing-cta">
        <div className="landing-cta-card">
          <div>
            <h2>Ready to move faster on your research slate?</h2>
            <p>
              Authenticate to step into the live environment, filter real numbers in seconds, and keep your personal workflow in sync with the latest data refresh.
            </p>
          </div>
          <div className="landing-cta-actions">
            <button type="button" className="cta-primary" onClick={handleLaunch}>
              Launch LinePicks
            </button>
            <button
              type="button"
              className="cta-secondary"
              onClick={() => document.getElementById('product')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Explore Features
            </button>
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="footer-brand">
          <img src="/logos/longLogo.png" alt="LinePicks mark" />
          <span>Research-grade sports data. Built by bettors for bettors.</span>
        </div>
        <div className="footer-copy">
          <p>
            Data shown is for informational purposes. Always wager responsibly. LinePicks surfaces data and tooling; you control execution and risk.
          </p>
          <span className="footer-meta">© {new Date().getFullYear()} LinePicks. All rights reserved.</span>
        </div>
      </footer>

      {isLoginOpen && (
        <div
          className="landing-login-overlay"
          role="dialog"
          aria-modal="true"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              handleCloseLogin();
            }
          }}
        >
          <div className="landing-login-modal">
            <LoginScreen onLogin={handleLoginSuccess} clerkUnavailable={!isClerkReady} onClose={handleCloseLogin} />
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;

